import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Spinner } from "components/atoms/Icon";
import { Button, DropDown, Typography } from "components/atoms";
import {
  useGetStudyPlannerEventsQuery,
  useGetStudyPlannerFormQuery,
  usePostStudyPlannerFormMutation,
} from "services/studyPlanner";
import { FetchError } from "components/molecules";
import { DatePicker } from "components/molecules/DatePicker";
import "./index.scss";
import { useAppSelector } from "hooks";
import { ai_mcat_chat_status } from "state/reducers/layoutSlice";
import classNames from "classnames";
import { BackButton } from "components/molecules";

interface IProps {
  theme: "light" | "dark";
}

export const StudyPlannerFormPage = ({ theme }: IProps) => {
  const navigate = useNavigate();
  const { aiMCATChatDrawerStatus } = useAppSelector(
    (state: { layout: { aiMCATChatDrawerStatus: ai_mcat_chat_status } }) =>
      state.layout
  );
  const [pickDate, setPickDate] = useState<string>();
  const [hoursPerWeek, setHoursPerWeek] = useState<
    null | string | { [key: number]: string }
  >(null);
  const [examDate, setExamDate] = useState<
    null | string | { [key: number]: string }
  >(null);

  const {
    isLoading,
    refetch: refetchGetForm,
    isError,
    data,
  } = useGetStudyPlannerFormQuery();

  const [PostForm, { isLoading: postFormIsLoading, isError: postFormIsError }] =
    usePostStudyPlannerFormMutation();

  const { refetch } = useGetStudyPlannerEventsQuery();

  const handleSubmit = async (
    startDate: any,
    examDate: any,
    hoursPerWeek: any
  ) => {
    try {
      await PostForm({
        StartDate: dayjs(startDate).format("YYYY-MM-DD"),
        ExamDate: examDate,
        HoursPerWeek: hoursPerWeek,
      })
        .unwrap()
        .then(() => {
          refetch().then(() => {
            navigate("/study-planner");
          });

          refetchGetForm();
        });
    } catch (error: any) {
      console.log("error : ", error);
    }
  };

  useEffect(() => {
    if (
      data &&
      data.ExamDate !== null &&
      data.StartDate !== null &&
      data.HoursPerWeek !== null &&
      data.Options.HoursPerWeek
    ) {
      setPickDate(data.StartDate);
      setExamDate(dayjs(data.ExamDate).format("MM/DD/YYYY"));
      setHoursPerWeek(data.HoursPerWeek.toString());
    }
  }, [data]);

  return (
    <div
      className={classNames(
        "study-planner-form-wrapper",
        aiMCATChatDrawerStatus === "closed"
          ? "study-planner-form-wrapper-ai-chat-drawer-closed"
          : ""
      )}
    >
      <BackButton theme={theme} />
      <div className="title-wrapper">
        {data && (
          <Typography
            variant="h1Med"
            label={`${
              data.ExamDate === null && data.StartDate === null
                ? "Create"
                : "Edit"
            } Your Study Plan`}
          />
        )}
        <Typography
          variant="h7Book"
          label={
            <>
              <span className="indicator-span">*</span>indicates required field
            </>
          }
          styles="indicator-require-text"
        />
      </div>
      {isLoading ? (
        <Spinner theme={theme} />
      ) : isError ? (
        <FetchError content="Oops! failed to load resources" />
      ) : (
        data && (
          <>
            <div className="input-group-wrapper">
              <Typography
                variant="h7Med"
                label={
                  <>
                    1. When do you plan to start studying?<span className="indicator-span">*</span>
                  </>
                }
                styles="form-question-custom-style"
              />
              <div className="input-wrapper">
                <DatePicker
                  setDateSelection={setPickDate}
                  defaultSelected={pickDate}
                  placeholder="Select a date"
                  inputFormat={"MM/DD/YYYY"}
                />
              </div>
            </div>

            <div className="input-group-wrapper">
              <Typography
                variant="h7Med"
                label={
                  <>
                    2. When are you taking MCAT?<span className="indicator-span">*</span>
                  </>
                }
                styles="form-question-custom-style"
              />
              <div className="input-wrapper">
                <DropDown
                  setDropDownSelection={setExamDate}
                  data={
                    data.Options &&
                    data.Options.ExamDates &&
                    Array.isArray(data.Options.ExamDates)
                      ? data.Options.ExamDates.map((val: any) =>
                          dayjs(val).format("MM/DD/YYYY")
                        )
                      : []
                  }
                  defaultSelected={examDate}
                  placeholder="Select a date"
                />
              </div>
            </div>

            <div className="input-group-wrapper">
              <Typography
                variant="h7Med"
                label={
                  <>
                    3. How many hours do you plan to study per week?<span className="indicator-span">*</span>
                  </>
                }
                styles="form-question-custom-style"
              />
              <div className="input-wrapper">
                <DropDown
                  setDropDownSelection={setHoursPerWeek}
                  data={
                    data.Options && data.Options.HoursPerWeek
                      ? Object.values(data.Options.HoursPerWeek)
                      : []
                  }
                  defaultSelected={
                    hoursPerWeek && typeof hoursPerWeek === "string"
                      ? data.Options.HoursPerWeek[hoursPerWeek]
                      : hoursPerWeek
                  }
                  placeholder="Select hours"
                />
              </div>
            </div>

            <div className="input-group-wrapper">
              <Button
                type="score"
                label="Submit"
                icon={false}
                styles="submit-btn"
                isDisabled={
                  !pickDate || hoursPerWeek === null || examDate === null
                }
                isLoading={postFormIsLoading}
                onClick={() => {
                  pickDate &&
                    examDate &&
                    Object.values(examDate).length > 0 &&
                    hoursPerWeek &&
                    Object.values(hoursPerWeek).length > 0 &&
                    handleSubmit(
                      pickDate,
                      typeof examDate === "string"
                        ? examDate
                        : Object.values(examDate)[0],
                      typeof hoursPerWeek === "string"
                        ? parseInt(hoursPerWeek)
                        : parseInt(Object.keys(hoursPerWeek)[0]) + 1
                    );

                }}
              />
            </div>

            {postFormIsError ? (
              <FetchError content="Oops! failed to submit the form." />
            ) : null}
          </>
        )
      )}
    </div>
  );
};
